import React from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTE } from '../routes';

/**
 * Handler for the back arrow on a Settings drill-in section. Lives in `hooks/` rather than
 * `pages/settings/` so a section's drill-in page can reach it without depending on the settings
 * page's own import graph.
 *
 * Prefers a real history pop (`navigate(-1)`): the area's route-level `PageTransition` derives
 * forward/back from the monotonic history index, so a *push* to `ROUTE.settings()` reads as a
 * forward navigation and slides the list<->section transition the wrong way, whereas a pop reads
 * as back and plays it in reverse. In the normal drill-in flow (index -> section) the entry being
 * popped IS the Settings index. Falls back to a direct route navigation only when there is no
 * in-app entry to pop (a deep link straight to a section), so the arrow still lands on the index
 * instead of leaving the app.
 */
export const useBackToSettings = (): (() => void) => {
  const navigate = useNavigate();
  return React.useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
    } else {
      navigate(ROUTE.settings());
    }
  }, [navigate]);
};

export default useBackToSettings;
